import { GameState, PieceType } from '../../types';
import { IAIPlayer, AIMoveResult, AIStats, AIPlayerType } from '../Core/IAIPlayer';
import { selectAstraPiece } from '../AstraAI/AstraDraft';
import { describeMove } from '../AstraAI/AstraRules';
import { observeForBlind } from './Observation';
import { BlindSearch } from './BlindSearch';

export const BLIND_VERSION='astra-blind-1.0.0';
export class AstraBlindAI implements IAIPlayer {
  readonly name=AIPlayerType.ZSTR_BLIND;
  readonly displayName='ZSTR Blind';
  readonly description='King-blind Zodiac Astra: searches all plausible enemy kings';
  private search=new BlindSearch();
  private stats:AIStats={movesCalculated:0,averageThinkingTime:0,nodesEvaluated:0};
  // Intentionally no live-engine reference, saved reports, or opponent king input.
  selectKing(kings:PieceType[]):PieceType{return kings[Math.floor(Math.random()*kings.length)];}
  selectPiece(available:PieceType[],opponent:PieceType[],own:PieceType[]):PieceType{return selectAstraPiece(available,opponent,own);}
  async selectMove(input:GameState,budgetMs:number):Promise<AIMoveResult>{
    const observation=observeForBlind(input);
    const r=this.search.find(observation,budgetMs);
    this.stats.averageThinkingTime=(this.stats.averageThinkingTime*this.stats.movesCalculated+r.elapsedMs)/(this.stats.movesCalculated+1);
    this.stats.movesCalculated++;this.stats.nodesEvaluated=(this.stats.nodesEvaluated||0)+r.nodes;
    // Only after choosing the move, reconnect IDs to the live executor's objects.
    // Live target king flags never return to search or its diagnostics.
    const pieces=new Map(input.board.flatMap(row=>row.flatMap(s=>s.Pieces.map(p=>[p.id,p] as const))));
    const move={...r.move.move,toPiece:r.move.move.toPiece?pieces.get(r.move.move.toPiece.id)||null:null,
      sideEffects:r.move.move.sideEffects.map(e=>({...e,piecesToEffect:e.piecesToEffect.map(p=>pieces.get(p.id)!).filter(Boolean)}))};
    return {fromPosition:r.move.fromPosition,move,thinkingTime:r.elapsedMs,confidence:0.5,evaluation:r.score,
      reasoning:`${BLIND_VERSION}: ${r.candidates.length} possible kings, depth ${r.depth}; ${describeMove(observation,r.move)}`,
      rootMoveScores:r.rootScores,
      evaluationBreakdown:{astra:{version:BLIND_VERSION,blind:true,budgetMs,depth:r.depth,nodes:r.nodes,qnodes:0,
        timedOut:r.timedOut,iterations:r.iterations,rootScores:r.rootScores,kingBeliefs:r.candidates,
        rootMoves:r.rootMoves,scoredRootMoves:r.rootScores.length,
        caveats:['Uniform probabilities over surviving possible kings; exposure never eliminates a candidate.',
          'Root moves are averaged across every hypothesis; deeper continuations are searched separately and may overestimate future knowledge.',
          'V1 hides king identity, not Octopus shadow identity; it is not yet a complete fog-of-war AI.']}}};
  }
  configure(config:Record<string,any>):void{this.search=new BlindSearch(Math.max(1,Math.min(10,config.maxDepth||6)),config.maxNodes??Infinity);}
  reset():void{this.stats={movesCalculated:0,averageThinkingTime:0,nodesEvaluated:0};}
  getStats():AIStats{return {...this.stats};}
}
